import { Schema } from 'effect';
import {
  HttpApiEndpoint,
  HttpApiGroup,
  HttpApiSchema,
} from 'effect/unstable/httpapi';
import {
  BadRequestError,
  InternalServerError,
  integrationProviderErrors,
} from './errors';
import { OptionalUserAuthMiddleware, UserAuthMiddleware } from './middleware';
import { DocumentTypeSchema } from './schemas/document-type';

export const DROPBOX_SCOPES = [
  'account_info.read',
  'files.metadata.read',
  'files.content.read',
] as const;
export const DROPBOX_CALLBACK_PATH = '/api/v2/integrations/dropbox/callback';
export const dropboxMobileCallbackUrl = (scheme: string) =>
  `${scheme}://dropbox-callback`;
export const DropboxMobileCompletion = Schema.Struct({
  state: Schema.String,
  code: Schema.String,
});

export const DropboxStatus = Schema.Struct({
  available: Schema.Boolean,
  connected: Schema.Boolean,
  display_name: Schema.NullOr(Schema.String),
});

export const DropboxEntry = Schema.Union([
  Schema.Struct({
    type: Schema.Literal('folder'),
    id: Schema.String,
    name: Schema.String,
  }),
  Schema.Struct({
    type: Schema.Literal('file'),
    id: Schema.String,
    name: Schema.String,
    size: Schema.Finite,
    modified: Schema.String,
    document_type: Schema.NullOr(DocumentTypeSchema),
  }),
]);
export type DropboxEntry = typeof DropboxEntry.Type;

export const DropboxListing = Schema.Struct({
  entries: Schema.Array(DropboxEntry),
  cursor: Schema.NullOr(Schema.String),
});
export type DropboxListing = typeof DropboxListing.Type;

export const DropboxImportSourceSchema = Schema.Struct({
  file_id: Schema.String,
  connection_id: Schema.String,
  document_type: DocumentTypeSchema,
});
export type DropboxImportSource = typeof DropboxImportSourceSchema.Type;

export const dropboxGroup = HttpApiGroup.make('Dropbox')
  .add(
    HttpApiEndpoint.post('StartDropboxMobile', '/mobile/start', {
      success: Schema.Struct({ url: Schema.String, state: Schema.String }),
      error: [...integrationProviderErrors, BadRequestError],
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.post('CompleteDropboxMobile', '/mobile/complete', {
      payload: DropboxMobileCompletion,
      success: DropboxStatus,
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('GetDropboxStatus', '/status', {
      success: DropboxStatus,
      error: InternalServerError,
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('ConnectDropbox', '/connect', {
      success: HttpApiSchema.Empty(302),
      error: [BadRequestError, InternalServerError],
    }).middleware(OptionalUserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('DropboxCallback', '/callback', {
      query: Schema.Struct({
        code: Schema.optional(Schema.String),
        state: Schema.optional(Schema.String),
        error: Schema.optional(Schema.String),
      }),
      success: HttpApiSchema.Empty(302),
      error: InternalServerError,
    }).middleware(OptionalUserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('ListDropboxFolder', '/folder', {
      query: Schema.Struct({
        folder_id: Schema.optional(Schema.String),
        cursor: Schema.optional(Schema.String),
      }),
      success: DropboxListing,
      error: [...integrationProviderErrors, BadRequestError],
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.get('SearchDropboxFiles', '/search', {
      query: Schema.Struct({
        q: Schema.String.check(Schema.isMaxLength(200)),
        cursor: Schema.optional(Schema.String),
      }),
      success: DropboxListing,
      error: [...integrationProviderErrors, BadRequestError],
    }).middleware(UserAuthMiddleware),
  )
  .add(
    HttpApiEndpoint.delete('DisconnectDropbox', '/disconnect', {
      success: Schema.Void.pipe(HttpApiSchema.status(204)),
      error: integrationProviderErrors,
    }).middleware(UserAuthMiddleware),
  )
  .prefix('/v2/integrations/dropbox');
